import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import { 
  Zap, User, Users, FileText, Check, ArrowRight, 
  Loader2, Sparkles, PartyPopper
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * QuickStartWizard - Onboarding ultra-rapide en 3 étapes
 * Objectif : Première facture créée en moins de 60 secondes
 */
const QuickStartWizard = ({ onComplete, onSkip }) => {
  const [step, setStep] = useState(0); // 0 = welcome, 1-3 = steps
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Données du formulaire
  const [formData, setFormData] = useState({
    // Étape 1: Nom & Activité
    userName: '',
    activity: '',
    // Étape 2: Client (pré-rempli)
    clientName: 'Mon Premier Client',
    clientEmail: '',
    // Étape 3: Facture (pré-rempli)
    productName: 'Prestation de service',
    productPrice: '500',
  });

  // Créer la facture via l'API
  const handleCreateInvoice = async () => {
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/quickstart/create-first-invoice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          user_name: formData.userName,
          activity: formData.activity,
          client_name: formData.clientName,
          client_email: formData.clientEmail,
          product_name: formData.productName,
          product_price: parseFloat(formData.productPrice) || 500
        })
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Erreur lors de la création');
      }
      
      const result = await response.json();
      
      // Succès ! Passer à l'écran final
      setStep(4);
      
      // Appeler onComplete après un délai pour montrer le succès
      setTimeout(() => {
        onComplete?.(result);
      }, 2500);
      
    } catch (err) {
      setError(err.message || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (step === 3) {
      handleCreateInvoice();
    } else {
      setStep(step + 1);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return formData.userName.trim().length >= 2;
      case 2:
        return formData.clientName.trim().length >= 2;
      case 3:
        return formData.productName.trim().length >= 2 && parseFloat(formData.productPrice) > 0;
      default:
        return true;
    }
  };

  // Animation variants
  const slideVariants = {
    enter: { x: 50, opacity: 0 },
    center: { x: 0, opacity: 1 },
    exit: { x: -50, opacity: 0 }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-slate-900/95 via-emerald-900/95 to-slate-900/95 backdrop-blur-sm">
      <Card className="w-full max-w-md mx-4 shadow-2xl border-0 overflow-hidden" data-testid="quickstart-wizard">
        {/* Header avec progression */}
        <div className="bg-gradient-to-r from-emerald-600 to-cyan-500 p-6 text-white">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur">
              <Zap className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold">SmartFacture AI</h2>
              <p className="text-emerald-100 text-sm">Votre première facture en 60 secondes</p>
            </div>
          </div>
          
          {/* Progress dots */}
          {step > 0 && step < 4 && (
            <div className="flex items-center gap-2">
              {[1, 2, 3].map((s) => (
                <div key={s} className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                    s < step ? 'bg-white text-emerald-600' :
                    s === step ? 'bg-white/30 text-white ring-2 ring-white' :
                    'bg-white/10 text-white/50'
                  }`}>
                    {s < step ? <Check className="w-4 h-4" /> : s}
                  </div>
                  {s < 3 && (
                    <div className={`w-8 h-1 mx-1 rounded ${s < step ? 'bg-white' : 'bg-white/20'}`} />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <CardContent className="p-6">
          <AnimatePresence mode="wait">
            {/* Écran d'accueil */}
            {step === 0 && (
              <motion.div
                key="welcome"
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                className="text-center py-4"
              >
                <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-emerald-100 to-cyan-100 rounded-2xl flex items-center justify-center">
                  <Sparkles className="w-10 h-10 text-emerald-600" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-3">
                  Bienvenue !
                </h3>
                <p className="text-slate-600 mb-6">
                  <span className="font-semibold text-emerald-600">3 étapes simples</span> pour créer votre première facture
                </p>
                
                <div className="space-y-3 text-left mb-8">
                  {[
                    { icon: User, text: 'Votre nom & activité' },
                    { icon: Users, text: 'Ajouter un client' },
                    { icon: FileText, text: 'Créer la facture' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                      <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-600 font-bold text-sm">
                        {i + 1}
                      </div>
                      <item.icon className="w-5 h-5 text-slate-400" />
                      <span className="text-slate-700">{item.text}</span>
                    </div>
                  ))}
                </div>

                <Button 
                  onClick={handleNext}
                  className="w-full h-14 text-lg bg-gradient-to-r from-emerald-600 to-cyan-500 hover:from-emerald-700 hover:to-cyan-600"
                  data-testid="start-wizard-btn"
                >
                  Créer ma première facture
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                
                <button 
                  onClick={onSkip}
                  className="mt-4 text-sm text-slate-500 hover:text-slate-700"
                >
                  Passer pour l'instant
                </button>
              </motion.div>
            )}

            {/* Étape 1: Nom & Activité */}
            {step === 1 && (
              <motion.div
                key="step1"
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                className="space-y-6"
              >
                <div className="text-center">
                  <div className="w-14 h-14 mx-auto mb-4 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <User className="w-7 h-7 text-emerald-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900">Comment vous appelez-vous ?</h3>
                  <p className="text-slate-500 text-sm mt-1">Ce nom apparaîtra sur vos factures</p>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <Input
                      value={formData.userName}
                      onChange={(e) => setFormData({ ...formData, userName: e.target.value })}
                      placeholder="Jean Dupont"
                      className="h-14 text-lg"
                      autoFocus
                      data-testid="user-name-input"
                    />
                  </div>
                  <div>
                    <Input
                      value={formData.activity}
                      onChange={(e) => setFormData({ ...formData, activity: e.target.value })}
                      placeholder="Consultant, Designer, Développeur..."
                      className="h-12"
                      data-testid="activity-input"
                    />
                    <p className="text-xs text-slate-400 mt-1">Optionnel</p>
                  </div>
                </div>

                <Button 
                  onClick={handleNext}
                  disabled={!canProceed()}
                  className="w-full h-12 bg-emerald-600 hover:bg-emerald-700"
                  data-testid="step1-next-btn"
                >
                  Continuer
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </motion.div>
            )}

            {/* Étape 2: Client */}
            {step === 2 && (
              <motion.div
                key="step2"
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                className="space-y-6"
              >
                <div className="text-center">
                  <div className="w-14 h-14 mx-auto mb-4 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <Users className="w-7 h-7 text-emerald-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900">À qui facturer ?</h3>
                  <p className="text-slate-500 text-sm mt-1">Nom de votre client</p>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <Input
                      value={formData.clientName}
                      onChange={(e) => setFormData({ ...formData, clientName: e.target.value })}
                      placeholder="Nom du client"
                      className="h-14 text-lg"
                      autoFocus
                      data-testid="client-name-input"
                    />
                  </div>
                  <div>
                    <Input
                      type="email"
                      value={formData.clientEmail}
                      onChange={(e) => setFormData({ ...formData, clientEmail: e.target.value })}
                      placeholder="email@client.com"
                      className="h-12"
                      data-testid="client-email-input"
                    />
                    <p className="text-xs text-slate-400 mt-1">Optionnel - pour envoyer la facture</p>
                  </div>
                </div>

                <Button 
                  onClick={handleNext}
                  disabled={!canProceed()}
                  className="w-full h-12 bg-emerald-600 hover:bg-emerald-700"
                  data-testid="step2-next-btn"
                >
                  Continuer
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </motion.div>
            )}

            {/* Étape 3: Facture */}
            {step === 3 && (
              <motion.div
                key="step3"
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                className="space-y-6"
              >
                <div className="text-center">
                  <div className="w-14 h-14 mx-auto mb-4 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <FileText className="w-7 h-7 text-emerald-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900">Votre prestation</h3>
                  <p className="text-slate-500 text-sm mt-1">Description et montant</p>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <Input
                      value={formData.productName}
                      onChange={(e) => setFormData({ ...formData, productName: e.target.value })}
                      placeholder="Ex: Prestation de service"
                      className="h-14 text-lg"
                      autoFocus
                      data-testid="product-name-input"
                    />
                  </div>
                  <div>
                    <div className="relative">
                      <Input
                        type="number"
                        value={formData.productPrice}
                        onChange={(e) => setFormData({ ...formData, productPrice: e.target.value })}
                        placeholder="500"
                        className="h-14 text-lg pr-12"
                        data-testid="product-price-input"
                      />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 font-medium">€</span>
                    </div>
                    <p className="text-xs text-slate-400 mt-1">Montant HT</p>
                  </div>
                </div>

                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    {error}
                  </div>
                )}

                <Button 
                  onClick={handleNext}
                  disabled={!canProceed() || loading}
                  className="w-full h-14 text-lg bg-gradient-to-r from-emerald-600 to-purple-600 hover:from-emerald-700 hover:to-purple-700"
                  data-testid="quickstart-create-invoice-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                      Création en cours...
                    </>
                  ) : (
                    <>
                      <FileText className="mr-2 w-5 h-5" />
                      Créer ma facture
                    </>
                  )}
                </Button>
              </motion.div>
            )}

            {/* Écran de succès */}
            {step === 4 && (
              <motion.div
                key="success"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="text-center py-8"
              >
                <motion.div 
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring" }}
                  className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full flex items-center justify-center shadow-lg shadow-emerald-500/30"
                >
                  <PartyPopper className="w-12 h-12 text-white" />
                </motion.div>
                
                <motion.h3 
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className="text-2xl font-bold text-slate-900 mb-2"
                >
                  Votre première facture est prête !
                </motion.h3>
                
                <motion.p 
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="text-slate-600"
                >
                  Bienvenue sur SmartFacture AI
                </motion.p>
                
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.6 }}
                  className="mt-6 flex items-center justify-center gap-2 text-emerald-600"
                >
                  <Sparkles className="w-5 h-5" />
                  <span className="font-medium">Redirection...</span>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </CardContent>
      </Card>
    </div>
  );
};

export default QuickStartWizard;
